import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plug, Sparkles } from "lucide-react";
import {
  Card,
  CardBody,
  Alert,
  Badge,
  Button,
  EmptyState,
  Input,
  Select,
  Skeleton,
  StatusPill,
  Textarea,
} from "@playbookai/ui";
import {
  AdminAboutCard,
  AdminRelatedCard,
  AdminSection,
} from "../AdminSection.js";
import {
  createIntegrationAdminApi,
  useApiClient,
  type IntegrationConnectionMode,
  type IntegrationTemplate,
  type IntegrationTemplateDetail,
} from "@playbookai/api-clients";
import {
  ADMIN_INTEGRATIONS_ROUTE,
  adminIntegrationRoute,
} from "../../../routes.js";
import {
  ManifestSettingsForm,
  renderableSettingsFields,
} from "./ManifestSettingsForm.js";
import { settingsFieldError } from "./manifest-render.js";
import { connectionModeHint, connectionModeLabel } from "./connection-mode.js";
import { PROTOCOL_LABELS } from "./integration-inventory.js";

/**
 * Adding an integration, as a page of its own at `/admin/integrations/new`.
 *
 * ## Why the whole flow lives in the URL
 *
 * `?template=` and `?integration=` carry every decision made so far, so the
 * flow survives the three things that reliably happen during it: an
 * accidental reload, a back button, and — the one that forced it — the OAuth
 * round trip, which leaves this tab entirely. Component state could carry none
 * of them, and an integration created and then lost to a refresh is the exact
 * "abandoned instance nobody can find" the inventory now has to be able to show.
 *
 * ## What this page does NOT do
 *
 * It does not re-implement the settings form, the access policy, the People
 * roster or the governed tool catalogue. Each already exists on the
 * integration's own detail tabs, so this page routes there rather than growing
 * a second copy that would drift. The steps it cannot finish itself it reports
 * the real state of and links to; none of them is ever ticked off on optimism.
 *
 * NO PROVIDER NAME REACHES THIS FILE. Every card's title, protocol and settings
 * form come from the template the server returned.
 */

export function AddIntegration(): ReactElement {
  const config = useApiClient();
  const api = useMemo(() => createIntegrationAdminApi(config), [config]);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const templateId = params.get("template");
  const createdId = params.get("integration");

  const setStep = (next: {
    template?: string | null;
    integration?: string | null;
  }): void => {
    const draft = new URLSearchParams(params);
    for (const [key, value] of Object.entries({
      template: next.template,
      integration: next.integration,
    })) {
      if (value === undefined) continue;
      if (value === null) draft.delete(key);
      else draft.set(key, value);
    }
    setParams(draft, { replace: true });
  };

  const templates = useQuery({
    queryKey: ["integration-templates"],
    queryFn: () => api.listTemplates(),
  });

  const persisted = useQuery({
    queryKey: ["tenant-integration", createdId],
    queryFn: () => api.getIntegration(createdId!),
    enabled: createdId !== null,
  });
  useEffect(() => {
    if (persisted.data)
      navigate(adminIntegrationRoute(persisted.data.id), { replace: true });
  }, [persisted.data, navigate]);

  return (
    <AdminSection
      icon={Plug}
      title="Add integration"
      description="Save the configuration, then connect your account and review access on the integration page."
      breadcrumb={
        <Button
          variant="ghost"
          className="-ms-2"
          onClick={() => navigate(ADMIN_INTEGRATIONS_ROUTE)}
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Integrations
        </Button>
      }
      meta={
        <>
          <AdminAboutCard title="About this page">
            Templates come from the connectors registered in this deployment;
            which providers exist is a property of the deployment, not of the
            console. A new integration starts switched off.
          </AdminAboutCard>
          <AdminRelatedCard ids={["genai", "audit"]} />
        </>
      }
    >
      <div className="space-y-6">
        {createdId !== null ? (
          persisted.isError ? (
            <Alert tone="critical" title="Integration unavailable">
              Return to the integration inventory to continue.
            </Alert>
          ) : (
            <Skeleton className="h-24 w-full" />
          )
        ) : templates.isError ? (
          <Alert tone="critical" title="Could not load the catalogue">
            Reload to try again.
          </Alert>
        ) : templates.isPending ? (
          <Skeleton className="h-48 w-full" />
        ) : templateId === null ? (
          <ChooseTemplate
            templates={templates.data}
            onChoose={(id) => setStep({ template: id })}
          />
        ) : (
          <ConfigureIntegration
            templateId={templateId}
            onBack={() => setStep({ template: null })}
            onCreated={(id) =>
              navigate(adminIntegrationRoute(id), { replace: true })
            }
          />
        )}
      </div>
    </AdminSection>
  );
}

/* ------------------------------------------------------------------ choose */

/**
 * MCP-first, and it is a PROTOCOL rule rather than a provider one.
 *
 * ADR-0071 is explicit that a connector's MCP and REST forms are two
 * independent integrations — neither falls back to, upgrades to or converts
 * into the other — and that the MCP one carries the broader governed catalogue
 * without a product release per provider operation. So where a connector offers
 * both, MCP is the one to start from. Nothing here reads a provider key; a
 * REST-only connector simply never shows the mark.
 */
function isRecommended(template: IntegrationTemplate): boolean {
  return template.protocol === "mcp";
}

function ChooseTemplate({
  templates,
  onChoose,
}: {
  readonly templates: readonly IntegrationTemplate[];
  readonly onChoose: (templateId: string) => void;
}): ReactElement {
  /* Recommended first, and STABLY: within each group the server's own order is
     preserved, so two connectors of the same protocol never swap places
     between renders. */
  const available = templates
    .filter((template) => template.status === "available")
    .map((template, index) => ({ template, index }))
    .sort(
      (a, b) =>
        Number(isRecommended(b.template)) - Number(isRecommended(a.template)) ||
        a.index - b.index,
    )
    .map((entry) => entry.template);

  if (available.length === 0) {
    /* Boxed, like every other empty state in the console. Free-standing it
       floated in the middle of the step with nothing to sit in. */
    return (
      <Card>
        <CardBody>
          <EmptyState
            title="No connectors registered"
            description="This deployment has no integration connectors available. An operator adds them at deploy time."
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <section aria-labelledby="choose-connector" className="space-y-3">
      <h3 id="choose-connector" className="text-sm font-semibold text-pb-text">
        Choose what to connect to
      </h3>
      <ul className="grid gap-3 sm:grid-cols-2">
        {available.map((template) => (
          <li key={template.id}>
            <Button
              variant="outline"
              onClick={() => onChoose(template.id)}
              className="h-full w-full flex-col items-start justify-start gap-2 rounded-xl border-pb-border bg-pb-card p-4 text-start hover:border-pb-accent/40"
            >
              <span className="flex w-full flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-pb-text">
                  {template.displayName}
                </span>
                {isRecommended(template) ? (
                  <Badge variant="soft">
                    <Sparkles className="size-3" aria-hidden="true" />
                    Recommended
                  </Badge>
                ) : null}
              </span>
              <span className="flex items-center gap-2">
                <StatusPill tone="neutral">
                  {PROTOCOL_LABELS[template.protocol]}
                </StatusPill>
                <span className="text-xs text-pb-text-muted">
                  v{template.version}
                </span>
              </span>
              <span className="text-xs text-pb-text-muted">
                {isRecommended(template)
                  ? "The governed remote tool catalogue. Its tools are reviewed here before anyone can use them."
                  : "The mapped capabilities this connector ships. A separate integration — it neither replaces nor upgrades into the other."}
              </span>
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* --------------------------------------------------------------- configure */

function ConfigureIntegration({
  templateId,
  onBack,
  onCreated,
}: {
  readonly templateId: string;
  readonly onBack: () => void;
  readonly onCreated: (integrationId: string) => void;
}): ReactElement {
  const config = useApiClient();
  const api = useMemo(() => createIntegrationAdminApi(config), [config]);
  const queryClient = useQueryClient();

  const template = useQuery({
    queryKey: ["integration-template", templateId],
    queryFn: () => api.getTemplate(templateId),
  });

  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [chosenMode, setChosenMode] = useState<IntegrationConnectionMode | "">(
    "",
  );
  const [settings, setSettings] = useState<Record<string, unknown>>({});

  const modes = template.data?.availableConnectionModes ?? [];
  /* Asked only when there is a decision to make. A template the server says can
     satisfy exactly one mode is not offering a choice, and rendering a
     one-option select would make the reader look for the alternative. It is
     still STATED below, because whose account the other system records is a
     consequence they should see either way. */
  const mode: IntegrationConnectionMode | "" =
    modes.length === 1 ? modes[0]! : chosenMode;

  const create = useMutation({
    mutationFn: () => {
      // Unreachable through the UI — Create stays disabled until a mode is
      // resolved. Stated as a narrowing rather than a cast, so a drift between
      // this state's type and the template's would fail to compile.
      if (mode === "") throw new Error("choose a connection mode");
      return api.createIntegration({
        templateId,
        displayName,
        ...(description === "" ? {} : { description }),
        connectionMode: mode,
        settings,
      });
    },
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({
        queryKey: ["tenant-integrations"],
      });
      onCreated(created.id);
    },
  });

  const reconcile = useMutation({
    mutationFn: async () =>
      (await api.listIntegrations()).filter(
        (row) =>
          row.displayName === displayName && row.templateId === templateId,
      ),
  });
  if (template.isError)
    return (
      <Alert tone="critical" title="Could not load this connector">
        Go back and choose again.
      </Alert>
    );
  if (template.isPending) return <Skeleton className="h-64 w-full" />;

  const detail: IntegrationTemplateDetail = template.data;
  const required = renderableSettingsFields(detail.manifest, "required");
  const settingsAnswered = required.every(
    (field) => settingsFieldError(field, settings[field.key]) === undefined,
  );

  return (
    <section
      aria-labelledby="configure-integration"
      className="space-y-5 rounded-xl border border-pb-border bg-pb-card p-5"
    >
      <div className="space-y-1">
        <h3
          id="configure-integration"
          className="text-sm font-semibold text-pb-text"
        >
          Configure {detail.displayName}
        </h3>
        <p className="text-xs text-pb-text-muted">
          Only what this connector genuinely needs. Everything else — access
          policy, who may use it, tool review — is on the integration once it
          exists.
        </p>
      </div>

      <Input
        id="integration-name"
        label="Name"
        hint="Unique in this workspace. Two accounts on the same system are two integrations, so name them for the team or account they serve."
        value={displayName}
        onChange={(event) => setDisplayName(event.target.value)}
      />

      <Textarea
        id="integration-description"
        label="Description"
        hint="Optional. What this connection is for, so the next admin does not have to guess."
        rows={2}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />

      {modes.length > 1 ? (
        <Select
          id="integration-mode"
          label="Connection mode"
          hint={connectionModeHint(chosenMode)}
          value={chosenMode}
          onChange={(next) =>
            setChosenMode(modes.find((option) => option === next) ?? "")
          }
          options={[
            { value: "", label: "Choose…" },
            ...modes.map((option) => ({
              value: option,
              label: connectionModeLabel(option),
            })),
          ]}
        />
      ) : mode === "" ? (
        <Alert tone="critical" title="This connector cannot be configured">
          The server reports no connection mode it can satisfy. An operator has
          to fix the connector's registration before an instance can exist.
        </Alert>
      ) : (
        <p className="text-xs text-pb-text-muted">
          <span className="font-medium text-pb-text">
            {connectionModeLabel(mode)}.
          </span>{" "}
          {connectionModeHint(mode)}
        </p>
      )}

      {required.length > 0 ? (
        <ManifestSettingsForm
          manifest={detail.manifest}
          values={settings}
          fields="required"
          onChange={(key, value) =>
            setSettings((current) => ({ ...current, [key]: value }))
          }
        />
      ) : null}

      {/* The server's own refusal, verbatim. It reports every policy violation
          at once and never quotes a submitted value, so it is safe to render
          and more useful than anything re-derived here. */}
      {create.error === null ? null : (
        <p role="alert" className="text-sm text-pb-red-text">
          {create.error.message}
        </p>
      )}

      {create.isError ? (
        <div className="space-y-2">
          <Button
            variant="secondary"
            loading={reconcile.isPending}
            onClick={() => reconcile.mutate()}
          >
            Check whether this integration was saved
          </Button>
          {reconcile.data?.map((row) => (
            <Button
              key={row.id}
              variant="secondary"
              onClick={() => onCreated(row.id)}
            >
              Open saved {row.displayName}
            </Button>
          ))}
          {reconcile.isSuccess && reconcile.data.length === 0 ? (
            <p className="text-sm text-pb-text-muted">
              No matching saved integration was found. Your form is preserved
              for retry.
            </p>
          ) : null}
          {reconcile.isError ? (
            <p role="alert">
              Could not check saved integrations. Try checking again before
              creating.
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          onClick={() => create.mutate()}
          disabled={
            displayName === "" ||
            mode === "" ||
            !settingsAnswered ||
            create.isPending
          }
        >
          {create.isPending ? "Creating…" : "Create integration"}
        </Button>
        <Button variant="ghost" onClick={onBack} disabled={create.isPending}>
          Back
        </Button>
      </div>
    </section>
  );
}

/* ------------------------------------------------------- connect & enable */
